import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './InvoiceDatePicker.css';

/**
 * InvoiceDatePicker - Specialized DatePicker for Invoice Creation screens
 * Full date selection (Day/Month/Year) for invoice dates and due dates
 * Returns ISO format (YYYY-MM-DD) for backend compatibility
 */
const InvoiceDatePicker = ({ 
  value, 
  onChange, 
  name, 
  id, 
  placeholder = 'Select date', 
  className = '', 
  required = false,
  label,
  disabled = false,
  readOnly = false,
}) => {
  // State for the calendar visibility
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  
  // State for year picker dropdown
  const [isYearPickerOpen, setIsYearPickerOpen] = useState(false);
  
  // State for current month being viewed
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // State for the selected date (from the value prop)
  const [selectedDate, setSelectedDate] = useState(null);
  
  // State for calendar position (for fixed positioning)
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  
  // Ref for the calendar popup to handle outside clicks
  const calendarRef = useRef(null);
  
  // Ref for year picker dropdown
  const yearPickerRef = useRef(null);
  
  // Ref for the input container to position the calendar
  const inputContainerRef = useRef(null);
  
  // Effect to parse the initial value if any
  useEffect(() => {
    if (value) {
      try {
        // Attempt to parse the date from the value (expecting YYYY-MM-DD)
        const parsedDate = parseInputDate(value);
        if (parsedDate && !isNaN(parsedDate.getTime())) {
          setSelectedDate(parsedDate);
          setCurrentMonth(new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1));
        }
      } catch (error) {
        console.error("Error parsing date:", error);
      }
    } else {
      setSelectedDate(null);
    }
  }, [value]);
  
  // Effect to handle clicking outside the calendar to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inCalendar = calendarRef.current && calendarRef.current.contains(event.target);
      const inInput = inputContainerRef.current && inputContainerRef.current.contains(event.target);
      if (!inCalendar && !inInput) {
        setIsCalendarOpen(false);
        setIsYearPickerOpen(false);
      }
    };
    
    // Add event listener when calendar is open
    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    // Remove event listener when component unmounts or calendar closes
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarOpen]);
  
  const CALENDAR_WIDTH = 280;
  const CALENDAR_HEIGHT = 340;

  const calcBoundedPosition = (rect) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top = rect.bottom + 4;
    let left = rect.left;

    if (top + CALENDAR_HEIGHT > vh) {
      top = rect.top - CALENDAR_HEIGHT - 4;
    }
    if (top < 0) top = 4;

    if (left + CALENDAR_WIDTH > vw) {
      left = vw - CALENDAR_WIDTH - 4;
    }
    if (left < 0) left = 4;

    return { top, left };
  };

  // Effect to update calendar position on scroll/resize
  useEffect(() => {
    if (!isCalendarOpen) return;
    
    const updatePosition = () => {
      if (inputContainerRef.current) {
        const rect = inputContainerRef.current.getBoundingClientRect();
        setCalendarPosition(calcBoundedPosition(rect));
      }
    };
    
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isCalendarOpen]);
  
  // Helper to parse the input date string (ISO format YYYY-MM-DD)
  const parseInputDate = (dateString) => {
    if (!dateString) return null;
    
    try {
      // ISO format (YYYY-MM-DD)
      if (dateString.includes('-')) {
        const dateParts = dateString.split('-');
        const year = parseInt(dateParts[0]);
        const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
        const day = parseInt(dateParts[2]);
        
        // Validate the parsed values
        if (year < 1900 || year > 2100 || month < 0 || month > 11 || day < 1 || day > 31) {
          return null;
        }
        
        const date = new Date(year, month, day);
        // Check if the date is valid (e.g., not 31st of February)
        if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
          return null;
        }
        
        return date;
      }
      
      return null;
    } catch (error) {
      console.error('Error parsing date:', error);
      return null;
    }
  };
  
  // Helper to format the date for display and backend (YYYY-MM-DD ISO format)
  const formatDate = (date) => {
    if (!date || isNaN(date.getTime())) return '';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    // Return ISO format (YYYY-MM-DD)
    return `${year}-${month}-${day}`;
  };
  
  // Helper to format the date for display in input (DD/MM/YYYY)
  const formatDisplayDate = (date) => {
    if (!date || isNaN(date.getTime())) return '';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  };
  
  // Handler for date selection
  const handleDateSelect = (day, month = currentMonth.getMonth()) => {
    const yearToUse = currentMonth.getFullYear();
    const newDate = new Date(yearToUse, month, day);
    setSelectedDate(newDate);
    
    // Update the current month being viewed to match the selected date
    setCurrentMonth(new Date(yearToUse, month, 1));
    
    // Format the date as ISO (YYYY-MM-DD) for backend
    const formattedDate = formatDate(newDate);
    
    if (onChange) {
      const event = {
        target: {
          name,
          value: formattedDate
        }
      };
      onChange(event);
    }
    
    // Close the calendar after selection
    setIsCalendarOpen(false);
  };
  
  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  
  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  
  // Toggle calendar visibility
  const toggleCalendar = () => {
    if (!disabled && !readOnly) {
      if (!isCalendarOpen && inputContainerRef.current) {
        const rect = inputContainerRef.current.getBoundingClientRect();
        setCalendarPosition(calcBoundedPosition(rect));
      }
      
      setIsCalendarOpen(!isCalendarOpen);
      
      // If opening the calendar and we have a selected date, set the current month to that date
      if (!isCalendarOpen && selectedDate) {
        setCurrentMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
      } else if (!isCalendarOpen && !selectedDate) {
        // If no date selected, start at current month
        setCurrentMonth(new Date());
      }
    }
  };
  
  // Handler for manual input in the text field
  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    
    // If the input value is empty, clear the selectedDate
    if (!inputValue || inputValue.trim() === '') {
      setSelectedDate(null);
      
      if (onChange) {
        onChange({
          target: {
            name,
            value: ''
          }
        });
      }
      return;
    }
    
    // For non-empty values, try to parse the date
    const inputDate = parseInputDate(inputValue);
    if (inputDate && !isNaN(inputDate.getTime())) {
      setSelectedDate(inputDate);
      
      // Format the date as ISO
      const formattedDate = formatDate(inputDate);
      
      if (onChange) {
        onChange({
          target: {
            name,
            value: formattedDate
          }
        });
      }
    } else {
      // If date is invalid, still call onChange but with the raw value
      if (onChange) {
        onChange(e);
      }
    }
  };
  
  // Generate calendar days for the current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    const firstDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    const totalDays = lastDay.getDate();
    
    // Initialize the calendar array with empty cells for the days before the first day of the month
    const calendarDays = Array(firstDayOfWeek).fill(null);
    
    // Add days of the month
    for (let day = 1; day <= totalDays; day++) {
      calendarDays.push(day);
    }
    
    return calendarDays;
  };
  
  // Month names for header
  const monthNames = [
    "January", "February", "March", "April", "May", "June", 
    "July", "August", "September", "October", "November", "December"
  ];
  
  // Day names for header
  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  
  // Get the name of the current month and year
  const currentMonthName = monthNames[currentMonth.getMonth()];
  const currentYear = currentMonth.getFullYear();
  
  // Render the calendar days
  const calendarDays = generateCalendarDays();
  
  // Check if a day is the selected day
  const isSelectedDay = (day) => {
    if (!selectedDate || !day) return false;
    
    return (
      selectedDate.getDate() === day && 
      selectedDate.getMonth() === currentMonth.getMonth() && 
      selectedDate.getFullYear() === currentMonth.getFullYear()
    );
  };
  
  // Check if a day is today
  const isToday = (day) => {
    if (!day) return false;
    
    const today = new Date();
    return (
      today.getDate() === day && 
      today.getMonth() === currentMonth.getMonth() && 
      today.getFullYear() === currentMonth.getFullYear()
    );
  };

  return (
    <div className={`invoice-date-picker-container ${className} ${isCalendarOpen ? 'calendar-open' : ''}`}>
      {label && (
        <label htmlFor={id || name} className="invoice-date-picker-label">
          {label} {required && <span className="required-field">*</span>}
        </label>
      )}
      
      <div className="invoice-date-picker-input-container" ref={inputContainerRef}>
        <input
          type="text"
          id={id || name}
          name={name}
          value={selectedDate ? formatDisplayDate(selectedDate) : ''}
          onChange={handleInputChange}
          placeholder={placeholder}
          className={`invoice-date-picker-input ${disabled ? 'disabled' : ''}`}
          disabled={disabled}
          required={required}
          readOnly={true}
          onClick={toggleCalendar}
          aria-label="Invoice date input"
        />
        
        <button 
          type="button" 
          onClick={toggleCalendar} 
          className={`invoice-date-picker-button ${disabled || readOnly ? 'disabled' : ''}`}
          disabled={disabled || readOnly}
          aria-label="Open calendar"
        >
          <CalendarIcon />
        </button>
      </div>
      
      {isCalendarOpen && ReactDOM.createPortal(
        <div 
          className="invoice-date-picker-calendar"
          ref={calendarRef}
          style={{
            top: `${calendarPosition.top}px`,
            left: `${calendarPosition.left}px`
          }}
        >
          <div className="invoice-calendar-header">
            <button 
              type="button" 
              className="invoice-calendar-nav-button" 
              onClick={goToPreviousMonth}
              aria-label="Previous month"
            >
              <span className="invoice-calendar-nav-icon">&#10094;</span>
            </button>
            
            <div className="invoice-calendar-header-title">
              <span className="invoice-calendar-month-name">{currentMonthName}</span>
              <div className="invoice-calendar-year-picker-container">
                <button 
                  type="button"
                  className="invoice-calendar-year-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsYearPickerOpen(!isYearPickerOpen);
                  }}
                  aria-label="Select year"
                >
                  {currentYear}
                </button>
                {isYearPickerOpen && (
                  <div 
                    className="invoice-calendar-year-dropdown"
                    ref={yearPickerRef}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="year-dropdown-scroll">
                      {Array.from({ length: 20 }, (_, i) => {
                        const year = new Date().getFullYear() + 5 - i;
                        return (
                          <button
                            key={year}
                            type="button"
                            className={`year-option ${year === currentYear ? 'selected' : ''}`}
                            onClick={() => {
                              setCurrentMonth(new Date(year, currentMonth.getMonth(), 1));
                              setIsYearPickerOpen(false);
                            }}
                          >
                            {year}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <button 
              type="button" 
              className="invoice-calendar-nav-button" 
              onClick={goToNextMonth}
              aria-label="Next month"
            >
              <span className="invoice-calendar-nav-icon">&#10095;</span>
            </button>
          </div>
          
          <div className="invoice-calendar-days-header">
            {dayNames.map((day, index) => (
              <div key={index} className="invoice-calendar-day-name">{day}</div>
            ))}
          </div>
          
          <div className="invoice-calendar-days-grid">
            {calendarDays.map((day, index) => (
              <div 
                key={index} 
                className={`invoice-calendar-day-cell ${!day ? 'empty-cell' : ''} ${isSelectedDay(day) ? 'selected' : ''} ${isToday(day) ? 'today' : ''}`}
                onClick={() => day && handleDateSelect(day)}
              >
                {day}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Calendar icon component
const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 2V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M16 2V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M3 9H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M3 8C3 6.34315 4.34315 5 6 5H18C19.6569 5 21 6.34315 21 8V18C21 19.6569 19.6569 21 18 21H6C4.34315 21 3 19.6569 3 18V8Z" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M8 13H8.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M12 13H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M16 13H16.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M8 17H8.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M12 17H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M16 17H16.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

export default InvoiceDatePicker;

